// ─── CALCUL DES JOURS DE CONGÉS ──────────────────────────────────────────────
// Trois méthodes conformes aux usages RH français :
//
// • OUVRÉS    : lundi→vendredi (5j/sem). Base légale Code du travail Art. L3141-3
//               Méthode la plus favorable au salarié. La plus courante.
// • OUVRABLES : lundi→samedi (6j/sem). Base historique avant 1982.
//               Légalement valide si prévu par convention collective.
// • CALENDAIRES : tous les jours (7j/sem). Utilisé pour certains congés spéciaux
//               (congé maternité, congé maladie, etc.)

const JOURS_FERIES_FR_2025_2028 = [
  // 2025
  '2025-01-01','2025-04-21','2025-05-01','2025-05-08','2025-05-29',
  '2025-06-09','2025-07-14','2025-08-15','2025-11-01','2025-11-11','2025-12-25',
  // 2026
  '2026-01-01','2026-04-06','2026-05-01','2026-05-08','2026-05-14',
  '2026-05-25','2026-07-14','2026-08-15','2026-11-01','2026-11-11','2026-12-25',
  // 2027
  '2027-01-01','2027-03-29','2027-05-01','2027-05-08','2027-05-17',
  '2027-05-28','2027-07-14','2027-08-15','2027-11-01','2027-11-11','2027-12-25',
  // 2028
  '2028-01-01','2028-04-17','2028-05-01','2028-05-08','2028-06-05',
  '2028-06-07','2028-07-14','2028-08-15','2028-11-01','2028-11-11','2028-12-25',
];
const FERIES_SET = new Set(JOURS_FERIES_FR_2025_2028);

/**
 * Calcule le nombre de jours de congé selon la méthode choisie.
 * Convention légale française (Art. L.3141-24 Code du travail) :
 *   - debut = 1er jour de congé (inclus)
 *   - fin   = dernier jour de congé (inclus) = veille du 1er jour travaillé
 *
 * ⚠️  L'UI utilise la convention B : le salarié saisit son jour de RETOUR.
 *     Utiliser calcJoursConge() en interne uniquement.
 *     Appeler calcJoursCongeRetour() depuis les composants UI.
 *
 * Temps partiel : si joursTravailes est fourni (ex: [1,4] pour lun+jeu),
 *   le décompte exclut les jours non travaillés du salarié (sauf mode calendaires).
 *   Cela reflète la règle : on ne décompte que les jours qui auraient été travaillés.
 *
 * @param {string} debut   - Date ISO 'YYYY-MM-DD' (1er jour de congé)
 * @param {string} fin     - Date ISO 'YYYY-MM-DD' (dernier jour de congé, bornes incluses)
 * @param {'ouvres'|'ouvrables'|'calendaires'} mode
 * @param {boolean} excluireFeries - Exclure les jours fériés (sauf calendaires)
 * @param {number[]|null} joursTravailes - Jours travaillés du salarié (0=dim…6=sam).
 *   null = temps plein (comportement inchangé).
 *   ex: [1,4] = lundi+jeudi uniquement.
 * @returns {number} Nombre de jours (>= 0)
 */
function calcJoursConge(debut, fin, mode = 'ouvres', excluireFeries = true, joursTravailes = null) {
  if (!debut || !fin) return 0;
  const d0 = new Date(debut + 'T12:00:00');
  const d1 = new Date(fin   + 'T12:00:00');
  if (d1 < d0) return 0;

  // Mode calendaires : on compte tous les jours, temps partiel sans effet
  if (mode === 'calendaires') {
    return Math.round((d1 - d0) / 86400000) + 1;
  }

  // Si joursTravailes fourni, on ignore les jours hors contrat du salarié
  const joursSet = joursTravailes && joursTravailes.length > 0
    ? new Set(joursTravailes)
    : null;

  let count = 0;
  const cur = new Date(d0);
  while (cur <= d1) {
    const dow = cur.getDay(); // 0=dim, 6=sam
    const ymd = cur.toISOString().slice(0, 10);
    const estFerie = excluireFeries && FERIES_SET.has(ymd);

    // Filtre temps partiel : si le salarié n'est pas censé travailler ce jour, on skip
    if (joursSet && !joursSet.has(dow)) {
      cur.setDate(cur.getDate() + 1);
      continue;
    }

    if (mode === 'ouvres'    && dow >= 1 && dow <= 5 && !estFerie) count++;
    if (mode === 'ouvrables' && dow >= 1 && dow <= 6 && !estFerie) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * Version UI — convention B (Art. L.3141-24) :
 *   Le salarié saisit son 1er jour de RETOUR au travail comme "dateFin".
 *   On calcule la vraie fin = veille du retour, puis on délègue à calcJoursConge.
 *
 * Exemple temps plein : pose lundi 7, retour lundi 14.
 *   → calcJoursCongeRetour('2025-06-07', '2025-06-14', 'ouvrables') = 6 j ✓
 *
 * Exemple temps partiel Lun+Jeu : pose lundi, retour jeudi.
 *   → veille du jeudi = mercredi
 *   → décompte lun+mar+mer, mais salarié ne travaille pas mar/mer
 *   → calcJoursCongeRetour('2025-06-02', '2025-06-05', 'ouvrables', true, [1,4]) = 1 j ✓
 *
 * @param {string} debut   - Date ISO 'YYYY-MM-DD' (1er jour de congé)
 * @param {string} retour  - Date ISO 'YYYY-MM-DD' (1er jour travaillé après congé)
 * @param {'ouvres'|'ouvrables'|'calendaires'} mode
 * @param {boolean} excluireFeries
 * @param {number[]|null} joursTravailes - Jours travaillés (0=dim…6=sam), null = temps plein
 * @returns {number} Nombre de jours (>= 0)
 */
function calcJoursCongeRetour(debut, retour, mode = 'ouvres', excluireFeries = true, joursTravailes = null) {
  if (!debut || !retour) return 0;
  const veille = new Date(retour + 'T12:00:00');
  veille.setDate(veille.getDate() - 1); // dernier jour de congé = veille du retour
  const finStr = veille.toISOString().slice(0, 10);
  return calcJoursConge(debut, finStr, mode, excluireFeries, joursTravailes);
}

/** Labels affichés à l'utilisateur */
const MODES_JOURS = {
  ouvres:      { label: 'Jours ouvrés',    short: 'ouvrés',    desc: 'Lundi → Vendredi (hors fériés)' },
  ouvrables:   { label: 'Jours ouvrables', short: 'ouvrables', desc: 'Lundi → Samedi (hors fériés)' },
  calendaires: { label: 'Jours calendaires', short: 'calendaires', desc: 'Tous les jours (sam., dim., fériés inclus)' },
};

/** Config par défaut si rien n'est configuré
 *  ⚠️ Ces valeurs sont STATIQUES — les valeurs live (SMIC, valeur point CCNS)
 *  sont chargées dynamiquement par useBaremes() dans Remunerations.jsx et Conges.jsx
 *  Modifier ici uniquement les modes de décompte (ouvres/ouvrables/calendaires)
 */
const MODES_JOURS_DEFAULT = {
  // Convention collective nationale du sport (CCNS) — Art. 4.3
  // ✅ CCNS IDCC 2511 : décompte en jours ouvrés (lun→ven) — plus favorable que légal
  // La loi permet ouvrables (lun→sam) mais la CCNS prévoit les ouvrés
  'Congés payés':              'ouvres',    // CCNS : jours ouvrés (lun→ven) — plus favorable
  'RTT':                       'ouvres',    // Idem
  'Congé sans solde':          'calendaires',
  'Congé maternité':           'calendaires',  // Art. L1225-17 — durée légale calendaire
  'Congé paternité':           'calendaires',  // Art. L1225-35 — 25j calendaires depuis 2021
  'Congé maladie':             'calendaires',
  'Récupération':              'ouvrables',
  // ── Événements familiaux — CCNS Art. 23 (non imputables sur CP) ─────────────
  'Mariage salarié':           'ouvres',       // CCNS Art. 23 : 5 jours ouvrés
  'PACS salarié':              'ouvres',       // CCNS Art. 23 : 4 jours ouvrés
  'Mariage enfant':            'ouvres',       // CCNS : 2j
  'Naissance / Adoption':      'ouvres',       // CCNS : 3j (hors congé paternité)
  'Décès conjoint / enfant':   'ouvres',       // CCNS : 5j
  'Décès père / mère':         'ouvres',       // CCNS : 3j
  'Décès frère / sœur':        'ouvres',       // CCNS : 1j
  'Déménagement':              'ouvres',       // CCNS : 1j
  'Enfant malade':             'ouvres',       // Art. L1225-61 — 3j/an
  'Autre':                     'ouvrables',
};

// ─── LOGO CMOB (base64) ─────────────────────────────────────────────────────
const LOGO_CMOB = "data:image/jpeg;base64,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";

// ─── CHARTE GRAPHIQUE CMOB ──────────────────────────────────────────────────
// Pantone Blue 072C → #003087 | Pantone Process Cyan C → #00AEEF

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bleu:#003087; --cyan:#00AEEF; --cyan2:#7DD6F5;
    --bg:#f0f6fc; --bg2:#ffffff; --bg3:#e6f2fb; --border:#c8dff0;
    --text:#1a2a3a; --muted:#6b8bad;
    --success:#1a7f37; --warning:#b45309; --danger:#b91c1c; --card:#ffffff;
  }
  /* ── Mode sombre ──────────────────────────────────────────────────────────── */
  [data-theme="dark"] {
    --bleu:#1a4ab0; --cyan:#00AEEF; --cyan2:#7DD6F5;
    --bg:#0f1520; --bg2:#1a2235; --bg3:#1e2a40; --border:#2a3a55;
    --text:#e2eaf5; --muted:#7a92b5;
    --success:#22c55e; --warning:#f59e0b; --danger:#ef4444; --card:#1a2235;
    --orange:#ea580c; --purple:#7c3aed;
  }
  body { font-family:'Nunito Sans','Lucida Sans','Trebuchet MS',sans-serif; background:var(--bg); color:var(--text); min-height:100vh; }
  .app { display:flex; min-height:100vh; overflow-x:hidden; position:relative; }

  /* ── LOGIN ── */
  .login-wrap { min-height:100vh; display:flex; align-items:center; justify-content:center; background:var(--bleu); padding:20px; }
  .login-card { background:#fff; border-radius:18px; padding:44px 40px; width:100%; max-width:400px; box-shadow:0 32px 80px rgba(0,0,0,0.25); }
  .login-logo { text-align:center; margin-bottom:28px; }
  .login-title { font-size:1.1rem; font-weight:700; color:var(--bleu); text-align:center; margin-bottom:22px; }
  .login-field { margin-bottom:16px; }
  .login-field label { display:block; font-size:0.7rem; color:var(--bleu); text-transform:uppercase; letter-spacing:0.08em; font-weight:700; margin-bottom:6px; }
  .login-field input, .login-field select { width:100%; background:var(--bg); border:1.5px solid var(--border); border-radius:8px; padding:10px 14px; color:var(--text); font-family:inherit; font-size:0.9rem; outline:none; transition:border-color 0.15s; }
  .login-field input:focus, .login-field select:focus { border-color:var(--cyan); box-shadow:0 0 0 3px rgba(0,174,239,0.12); }
  .login-btn { width:100%; padding:12px; background:var(--bleu); color:#fff; border:none; border-radius:8px; font-size:0.95rem; font-weight:700; cursor:pointer; font-family:inherit; transition:background 0.15s; margin-top:6px; }
  .login-btn:hover { background:#002070; }
  .login-error { background:rgba(185,28,28,0.08); border:1px solid rgba(185,28,28,0.25); color:var(--danger); padding:10px 14px; border-radius:7px; font-size:0.83rem; font-weight:600; margin-bottom:14px; text-align:center; }
  .login-role-badge { display:inline-flex; align-items:center; gap:6px; padding:4px 12px; border-radius:20px; font-size:0.75rem; font-weight:700; }

  /* ── SIDEBAR ── */
  .sidebar { width:255px; min-height:100vh; background:var(--bleu); display:flex; flex-direction:column; position:sticky; top:0; height:100vh; overflow-y:auto; box-shadow:4px 0 20px rgba(0,48,135,0.15); }
  .sidebar-logo { padding:22px 20px 18px; border-bottom:1px solid rgba(255,255,255,0.15); display:flex; flex-direction:column; gap:4px; }
  .logo-cmob { font-size:2.2rem; font-weight:700; color:#fff; letter-spacing:-0.02em; line-height:1; font-style:italic; }
  .logo-cmob span { color:var(--cyan); }
  .logo-sub { font-size:0.68rem; color:rgba(255,255,255,0.6); letter-spacing:0.06em; text-transform:uppercase; line-height:1.4; }
  .sidebar-user { padding:10px 18px 12px; border-bottom:1px solid rgba(255,255,255,0.1); }
  .sidebar-user-name { font-size:0.88rem; font-weight:700; color:#fff; }
  .sidebar-user-role { display:inline-flex; align-items:center; gap:5px; margin-top:4px; padding:3px 10px; border-radius:12px; font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; }
  .role-rh   { background:rgba(0,174,239,0.25); color:var(--cyan2); }
  .role-admin { background:rgba(124,58,237,0.3); color:#c4b5fd; }
  .role-manager { background:rgba(124,58,237,0.25); color:#c4b5fd; }
  .role-sal  { background:rgba(255,255,255,0.15); color:rgba(255,255,255,0.8); }
  .nav-section { padding:10px 10px 4px; }
  .nav-label { font-size:0.6rem; color:rgba(255,255,255,0.55); text-transform:uppercase; letter-spacing:0.14em; padding:0 10px 8px; font-weight:700; }
  .nav-item { display:flex; align-items:center; gap:9px; padding:9px 12px; border-radius:7px; cursor:pointer; font-size:0.82rem; font-weight:600; color:var(--cyan); transition:all 0.15s; border:1px solid transparent; margin-bottom:1px; text-decoration:none; }
  .nav-item:hover { background:rgba(0,174,239,0.18); color:#fff; }
  .nav-item.active { background:rgba(255,255,255,0.15); color:#fff; border-color:rgba(255,255,255,0.3); }
  .nav-icon { font-size:1rem; width:20px; text-align:center; }
  .sidebar-footer { margin-top:auto; padding:10px 18px 14px; border-top:1px solid rgba(255,255,255,0.1); }
  .sidebar-footer-addr { font-size:0.66rem; color:rgba(255,255,255,0.32); line-height:1.6; }
  .btn-logout { width:100%; padding:7px; background:rgba(255,255,255,0.08); color:rgba(255,255,255,0.7); border:1px solid rgba(255,255,255,0.15); border-radius:7px; font-size:0.78rem; font-weight:700; cursor:pointer; font-family:inherit; transition:all 0.15s; margin-bottom:10px; }
  .btn-logout:hover { background:rgba(255,255,255,0.15); color:#fff; }

  /* ── MAIN ── */
  .main { flex:1; display:flex; flex-direction:column; overflow:hidden; min-width:0; }
  .topbar { padding:16px 28px; border-bottom:2px solid var(--border); background:var(--bg2); display:flex; align-items:center; justify-content:space-between; box-shadow:0 2px 8px rgba(0,48,135,0.06); }
  .topbar-left h2 { font-size:1.4rem; font-weight:700; color:var(--bleu); letter-spacing:-0.01em; }
  .topbar-sub { font-size:0.78rem; color:var(--muted); margin-top:2px; }
  .topbar-right { display:flex; align-items:center; gap:10px; }
  .topbar-badge { padding:6px 14px; background:var(--bg3); border:1px solid var(--border); border-radius:20px; font-size:0.75rem; color:var(--bleu); font-weight:600; }
  .topbar-role { padding:5px 12px; border-radius:20px; font-size:0.73rem; font-weight:700; }
  .topbar-role.rh    { background:rgba(0,174,239,0.12); color:#0077bb; border:1px solid rgba(0,174,239,0.25); }
  .topbar-role.admin { background:rgba(124,58,237,0.12); color:#6d28d9; border:1px solid rgba(124,58,237,0.25); }
  .topbar-role.sal { background:rgba(26,127,55,0.1);  color:var(--success); border:1px solid rgba(26,127,55,0.2); }
  .content { padding:24px 28px; flex:1; overflow-y:auto; overflow-x:hidden; }

  /* ── STATS ── */
  .stats-row { display:grid; grid-template-columns:repeat(auto-fit,minmax(155px,1fr)); gap:14px; margin-bottom:24px; }
  /* ── Classes grille responsive — à utiliser à la place des styles inline fixes ── */
  .grid-2  { display:grid; grid-template-columns:1fr 1fr; gap:13px; }
  .grid-3  { display:grid; grid-template-columns:repeat(3,1fr); gap:13px; }
  .grid-4  { display:grid; grid-template-columns:repeat(4,1fr); gap:13px; }
  .grid-auto { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:13px; }
  .stat-card { background:var(--card); border:1px solid var(--border); border-radius:11px; padding:18px 20px; position:relative; overflow:hidden; box-shadow:0 2px 8px rgba(0,48,135,0.06); }
  .stat-card::after { content:''; position:absolute; top:0; left:0; right:0; height:4px; background:var(--bleu); border-radius:11px 11px 0 0; }
  .stat-card.cyan::after    { background:var(--cyan); }
  .stat-card.success::after { background:var(--success); }
  .stat-card.danger::after  { background:var(--danger); }
  .stat-card.warning::after { background:var(--warning); }
  .stat-card.purple::after  { background:var(--purple); }
  .stat-card.orange::after  { background:var(--orange); }
  .stat-value { font-size:2rem; font-weight:700; color:var(--bleu); line-height:1; margin-bottom:4px; }
  .stat-card.cyan .stat-value    { color:#0077bb; }
  .stat-card.success .stat-value { color:var(--success); }
  .stat-card.danger .stat-value  { color:var(--danger); }
  .stat-card.warning .stat-value { color:var(--warning); }
  .stat-card.purple .stat-value  { color:var(--purple); }
  .stat-card.orange .stat-value  { color:var(--orange); }
  .stat-label { font-size:0.72rem; color:var(--muted); text-transform:uppercase; letter-spacing:0.08em; font-weight:600; }

  /* ── CARDS ── */
  .card { background:var(--card); border:1px solid var(--border); border-radius:11px; overflow:hidden; margin-bottom:18px; box-shadow:0 2px 8px rgba(0,48,135,0.06); }
  .card-header { padding:13px 18px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; background:linear-gradient(to right,rgba(0,48,135,0.03),transparent); }
  .card-title { font-size:0.95rem; font-weight:700; color:var(--bleu); }

  /* ── TABLES ── */
  table { width:100%; border-collapse:collapse; }
  th { padding:9px 14px; text-align:left; font-size:0.66rem; color:var(--bleu); text-transform:uppercase; letter-spacing:0.1em; font-weight:700; border-bottom:2px solid var(--border); background:rgba(0,48,135,0.03); }
  thead.bleu th { color:#fff !important; background:transparent; border-bottom:none; text-transform:none; letter-spacing:normal; }
  td { padding:10px 14px; font-size:0.855rem; border-bottom:1px solid #eef3f9; }
  tr:last-child td { border-bottom:none; }
  tr:hover td { background:rgba(0,174,239,0.04); }

  /* ── BADGES ── */
  .badge { display:inline-flex; align-items:center; padding:3px 9px; border-radius:20px; font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; white-space:nowrap; }
  .badge-bleu   { background:rgba(0,48,135,0.1);   color:var(--bleu); }
  .badge-cyan   { background:rgba(0,174,239,0.12);  color:#0077bb; }
  .badge-green  { background:rgba(26,127,55,0.1);   color:var(--success); }
  .badge-red    { background:rgba(185,28,28,0.1);   color:var(--danger); }
  .badge-yellow { background:rgba(180,83,9,0.1);    color:var(--warning); }
  .badge-gray   { background:rgba(107,139,173,0.1); color:var(--muted); }
  .badge-purple { background:rgba(124,58,237,0.1);  color:var(--purple); }
  .badge-orange { background:rgba(234,88,12,0.1);   color:var(--orange); }

  /* ── ALERTS ── */
  .alert-banner { display:flex; align-items:center; gap:10px; padding:10px 16px; border-radius:8px; font-size:0.82rem; margin-bottom:14px; font-weight:600; }
  .alert-danger  { background:rgba(185,28,28,0.08);  border:1px solid rgba(185,28,28,0.25);  color:var(--danger); }
  .alert-warning { background:rgba(180,83,9,0.08);   border:1px solid rgba(180,83,9,0.25);   color:var(--warning); }
  .alert-info    { background:rgba(0,174,239,0.08);  border:1px solid rgba(0,174,239,0.25);  color:#0077bb; }

  /* ── BUTTONS ── */
  .btn { padding:7px 16px; border-radius:7px; font-size:0.82rem; font-weight:700; cursor:pointer; border:none; transition:all 0.15s; font-family:inherit; }
  .btn-primary   { background:var(--bleu); color:#fff; }
  .btn-primary:hover { background:#002070; }
  .btn-cyan      { background:var(--cyan); color:#fff; }
  .btn-cyan:hover { background:#009ad5; }
  .btn-secondary { background:#fff; color:var(--bleu); border:1.5px solid var(--border); }
  .btn-secondary:hover { background:var(--bg3); }
  .btn-danger    { background:rgba(185,28,28,0.08); color:var(--danger); border:1px solid rgba(185,28,28,0.2); }
  .btn-danger:hover { background:rgba(185,28,28,0.15); }
  .btn-sm  { padding:4px 9px; font-size:0.75rem; }
  .btn-group { display:flex; gap:5px; flex-wrap:wrap; }

  /* ── FORMS ── */
  .modal-overlay { position:fixed; inset:0; background:rgba(0,20,60,0.45); backdrop-filter:blur(4px); z-index:100; display:flex; align-items:center; justify-content:center; padding:20px; }
  .modal { background:var(--bg2); border:1px solid var(--border); border-radius:14px; padding:26px; width:100%; max-width:560px; max-height:88vh; overflow-y:auto; box-shadow:0 24px 60px rgba(0,48,135,0.2); }
  .modal-title { font-size:1.15rem; font-weight:700; color:var(--bleu); padding-bottom:14px; border-bottom:2px solid var(--border); margin-bottom:18px; display:flex; justify-content:space-between; align-items:center; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:13px; }
  .form-group { display:flex; flex-direction:column; gap:5px; min-width:0; }
  .form-group.full { grid-column:1/-1; }
  label { font-size:0.7rem; color:var(--bleu); text-transform:uppercase; letter-spacing:0.08em; font-weight:700; }
  input, select, textarea { background:var(--bg); border:1.5px solid var(--border); border-radius:7px; padding:8px 11px; color:var(--text); font-family:inherit; font-size:0.855rem; transition:border-color 0.15s; outline:none; }
  input:focus, select:focus, textarea:focus { border-color:var(--cyan); box-shadow:0 0 0 3px rgba(0,174,239,0.1); }
  textarea { resize:vertical; min-height:75px; }

  /* ── SEARCH ── */
  .search-bar { display:flex; align-items:center; gap:9px; margin-bottom:18px; flex-wrap:wrap; }
  .search-bar input { flex:1; min-width:180px; }

  /* ── AVATAR ── */
  .avatar { width:32px; height:32px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-size:0.75rem; font-weight:700; flex-shrink:0; color:#fff; }

  /* ── TABS ── */
  .tabs { display:flex; gap:2px; margin-bottom:18px; background:var(--bg3); padding:3px; border-radius:9px; border:1px solid var(--border); width:fit-content; flex-wrap:wrap; }
  .tab { padding:6px 14px; border-radius:6px; font-size:0.79rem; font-weight:700; cursor:pointer; color:var(--muted); transition:all 0.15s; }
  .tab.active { background:var(--bleu); color:#fff; }
  .tab:hover:not(.active) { color:var(--bleu); }

  /* ── CALENDAR ── */
  .cal-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:3px; margin-top:10px; }
  .cal-day-name { text-align:center; font-size:0.65rem; color:var(--bleu); text-transform:uppercase; padding:5px; font-weight:700; letter-spacing:0.06em; }
  .cal-day { min-height:78px; background:var(--bg); border:1.5px solid var(--border); border-radius:7px; padding:5px; transition:all 0.15s; }
  .cal-day:hover { border-color:var(--cyan); }
  .cal-day.other-month { opacity:0.3; }
  .cal-day.today { border-color:var(--bleu); background:rgba(0,48,135,0.04); }
  .cal-day-num { font-size:0.75rem; color:var(--muted); margin-bottom:3px; font-weight:600; }
  .cal-day.today .cal-day-num { color:var(--bleu); font-weight:700; }
  .cal-event { font-size:0.6rem; padding:2px 4px; border-radius:3px; margin-top:2px; background:rgba(0,48,135,0.1); color:var(--bleu); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-weight:600; }
  .cal-event.cyan { background:rgba(0,174,239,0.15); color:#0077bb; }
  .cal-event.red  { background:rgba(185,28,28,0.1);  color:var(--danger); }
  .cal-nav { display:flex; align-items:center; gap:14px; }
  .cal-nav h3 { font-size:1.2rem; min-width:200px; text-align:center; color:var(--bleu); font-weight:700; }

  /* ── MISC ── */
  .progress-bar { background:var(--bg3); border-radius:20px; height:6px; overflow:hidden; }
  .progress-fill { height:100%; background:var(--cyan); border-radius:20px; transition:width 0.5s; }
  .empty { text-align:center; padding:48px; color:var(--muted); }
  .empty-icon { font-size:2.2rem; margin-bottom:10px; }
  .divider-cyan { width:36px; height:3px; background:linear-gradient(to right,var(--bleu),var(--cyan)); border-radius:2px; margin-bottom:18px; }
  .divider-cyan:has(span) { width:auto; height:auto; background:none; display:flex; align-items:center; gap:10px; margin-bottom:16px; }
  .divider-cyan:has(span)::before { content:''; display:block; width:36px; min-width:36px; height:3px; background:linear-gradient(to right,var(--bleu),var(--cyan)); border-radius:2px; flex-shrink:0; }
  .divider-cyan:has(span) span { font-size:0.85rem; font-weight:700; color:var(--bleu); white-space:normal; word-break:break-word; }
  .topbar-sync-badge { font-size:0.72rem; padding:2px 8px; border-radius:12px; border:1px solid; font-weight:700; }
  .doc-chip { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; border-radius:6px; font-size:0.75rem; font-weight:600; margin:3px; background:var(--bg3); color:var(--bleu); border:1px solid var(--border); }
  .doc-chip.expiring { background:rgba(180,83,9,0.08); color:var(--warning); border-color:rgba(180,83,9,0.2); }
  .doc-chip.expired  { background:rgba(185,28,28,0.08); color:var(--danger);  border-color:rgba(185,28,28,0.2); }

  /* ── WORKFLOW STEPS ── */
  .workflow-steps { display:flex; align-items:center; gap:0; margin-bottom:22px; }
  .wf-step { flex:1; display:flex; flex-direction:column; align-items:center; position:relative; }
  .wf-step:not(:last-child)::after { content:''; position:absolute; top:17px; left:calc(50% + 18px); right:calc(-50% + 18px); height:2px; background:var(--border); z-index:0; }
  .wf-step.done:not(:last-child)::after  { background:var(--success); }
  .wf-step.active:not(:last-child)::after { background:var(--cyan); }
  .wf-dot { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:1rem; font-weight:700; z-index:1; border:2px solid var(--border); background:var(--bg2); color:var(--muted); }
  .wf-step.done  .wf-dot { background:var(--success); border-color:var(--success); color:#fff; }
  .wf-step.active .wf-dot { background:var(--cyan); border-color:var(--cyan); color:#fff; box-shadow:0 0 0 4px rgba(0,174,239,0.15); }
  .wf-step.rejected .wf-dot { background:var(--danger); border-color:var(--danger); color:#fff; }
  .wf-label { font-size:0.68rem; font-weight:700; color:var(--muted); margin-top:5px; text-align:center; text-transform:uppercase; letter-spacing:0.05em; }
  .wf-step.done  .wf-label { color:var(--success); }
  .wf-step.active .wf-label { color:var(--cyan); }
  .wf-step.rejected .wf-label { color:var(--danger); }
  .wf-comment { font-size:0.75rem; color:var(--muted); margin-top:3px; font-style:italic; text-align:center; max-width:100px; }

  /* ── BASE DOCUMENTAIRE ── */
  .doc-card { background:var(--card); border:1px solid var(--border); border-radius:10px; padding:16px 18px; margin-bottom:10px; display:flex; align-items:flex-start; gap:14px; transition:box-shadow 0.15s; cursor:default; }
  .doc-card:hover { box-shadow:0 4px 16px rgba(0,48,135,0.1); border-color:var(--cyan); }
  .doc-icon { width:42px; height:42px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:1.3rem; flex-shrink:0; }
  .doc-body { flex:1; min-width:0; }
  .doc-title { font-size:0.92rem; font-weight:700; color:var(--bleu); margin-bottom:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .doc-meta  { font-size:0.75rem; color:var(--muted); margin-bottom:6px; }
  .doc-desc  { font-size:0.8rem; color:var(--text); opacity:0.8; margin-bottom:8px; }
  .doc-tags  { display:flex; flex-wrap:wrap; gap:4px; }
  .doc-tag   { padding:2px 8px; border-radius:12px; font-size:0.66rem; font-weight:700; background:var(--bg3); color:var(--bleu); border:1px solid var(--border); text-transform:uppercase; }
  .doc-actions { display:flex; flex-direction:column; gap:5px; align-items:flex-end; flex-shrink:0; }
  .cat-header { display:flex; align-items:center; gap:10px; padding:10px 0 8px; margin-bottom:8px; border-bottom:2px solid var(--border); }
  .cat-title  { font-size:1rem; font-weight:700; color:var(--bleu); }
  .cat-count  { font-size:0.72rem; color:var(--muted); font-weight:600; }

  .perso-header { background:linear-gradient(135deg,var(--bleu),#005299); border-radius:12px; padding:22px 24px; margin-bottom:20px; color:#fff; display:flex; align-items:center; gap:18px; }
  .perso-avatar { width:54px; height:54px; border-radius:50%; background:rgba(255,255,255,0.2); display:flex; align-items:center; justify-content:center; font-size:1.4rem; font-weight:700; flex-shrink:0; }
  .perso-name { font-size:1.3rem; font-weight:700; }
  .perso-sub  { font-size:0.82rem; opacity:0.75; margin-top:3px; }

  /* ── RESPONSIVE DESIGN ── */
  /* Base : bouton hamburger caché sur desktop, sidebar visible */
  .mobile-menu-btn { display:none; position:fixed; bottom:18px; right:18px; z-index:200; width:52px; height:52px; border-radius:50%; background:var(--bleu); color:#fff; border:none; font-size:1.4rem; cursor:pointer; box-shadow:0 4px 16px rgba(0,48,135,0.4); }
  .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,20,60,0.5); z-index:149; }

  /* Images toujours responsives */
  img { max-width:100%; height:auto; }

  /* Scroll smooth */
  html { scroll-behavior:smooth; }

  /* ── TABLETTE (769px - 1024px) ── */
  @media (min-width:769px) and (max-width:1024px) {
    .grid-4 { grid-template-columns:repeat(2,1fr) !important; }
    .grid-3 { grid-template-columns:repeat(2,1fr) !important; }
    .sidebar { position:fixed; left:0; top:0; bottom:0; width:220px; height:100vh; z-index:150; transform:translateX(-100%); transition:transform 0.25s ease; }
    .sidebar.open { transform:translateX(0); box-shadow:4px 0 30px rgba(0,0,0,0.3); }
    .sidebar-overlay.open { display:block; }
    .main { width:100%; min-width:0; }
    .mobile-menu-btn { display:flex; align-items:center; justify-content:center; }
    .form-grid { grid-template-columns:1fr 1fr; }
    .stats-row { grid-template-columns:repeat(3,1fr) !important; gap:10px; }
    .card { padding:16px; }
    table { font-size:0.8rem; }
    th,td { padding:8px 10px; }
    .topbar { padding:12px 20px; }
    .topbar-left h2 { font-size:1.2rem; }
    .topbar-right .topbar-badge { display:none; }
    .topbar-sync-badge { display:none; }
    .modal { max-width:600px; }
    .org-node { min-width:110px; max-width:140px; padding:10px 14px; }
    .planning-week-grid { overflow-x:auto; -webkit-overflow-scrolling:touch; display:block; }
  }

  /* ── MOBILE STANDARD (481px - 768px) ── */
  @media (min-width:481px) and (max-width:768px) {
    .grid-2, .grid-3, .grid-4 { grid-template-columns:1fr !important; gap:10px; }
    .grid-auto { grid-template-columns:1fr !important; }
    .sidebar { position:fixed; left:0; top:0; bottom:0; z-index:150; transform:translateX(-100%); transition:transform 0.25s ease; height:100vh; width:270px; }
    .sidebar.open { transform:translateX(0); box-shadow:4px 0 30px rgba(0,0,0,0.3); }
    .sidebar-overlay.open { display:block; }
    .main { width:100%; min-width:0; }
    .mobile-menu-btn { display:flex; align-items:center; justify-content:center; }
    .form-grid { grid-template-columns:1fr; }
    .content { padding:12px 14px; }
    .topbar { padding:10px 14px; }
    .topbar-left h2 { font-size:1.05rem; }
    .topbar-right .topbar-badge { display:none; }
    .topbar-sync-badge { display:none; }
    .topbar-right { gap:8px; }
    .topbar-role { font-size:0.7rem; padding:4px 10px; }
    .workflow-steps { flex-direction:column; gap:10px; }
    .wf-step:not(:last-child)::after { display:none; }
    .stats-row { grid-template-columns:repeat(2,1fr) !important; gap:8px; }
    .card { overflow-x:auto; }
    table { font-size:0.75rem; min-width:480px; }
    th,td { padding:6px 8px; }
    .cal-grid { gap:1px; }
    .cal-day { min-height:50px; }
    .two-col-grid { grid-template-columns:1fr !important; }
    .tabs { overflow-x:auto; overflow-y:hidden; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
    .tabs::-webkit-scrollbar { display:none; }
    .tab-btn { white-space:nowrap; flex-shrink:0; }
    .search-bar { flex-direction:column; gap:8px; }
    .search-bar input { width:100%; min-width:unset; }
    .modal-overlay { padding:10px; align-items:flex-end; }
    .modal { border-radius:14px 14px 0 0; max-height:90vh; }
    .btn-group { flex-wrap:wrap; }
    .btn { min-height:44px; }
    .menu-item { min-height:48px; }
    .tab-btn { min-height:44px; }
    input,select,textarea { font-size:16px; }
    .doc-card { flex-direction:column; gap:10px; }
    .doc-actions { flex-direction:row; align-items:center; justify-content:flex-start; }
    .perso-header { padding:16px 18px; gap:12px; }
    .perso-name { font-size:1.1rem; }
    .perso-avatar { width:46px; height:46px; font-size:1.2rem; }
  }

  /* ── SMARTPHONE PORTRAIT (≤ 480px) ── */
  @media (max-width:480px) {
    .grid-2, .grid-3, .grid-4 { grid-template-columns:1fr !important; gap:8px; }
    .grid-auto { grid-template-columns:1fr !important; }
    .sidebar { position:fixed; left:0; top:0; bottom:0; z-index:150; transform:translateX(-100%); transition:transform 0.25s ease; height:100vh; width:280px; }
    .sidebar.open { transform:translateX(0); box-shadow:4px 0 30px rgba(0,0,0,0.3); }
    .sidebar-overlay.open { display:block; }
    .main { width:100%; min-width:0; }
    .mobile-menu-btn { display:flex; align-items:center; justify-content:center; width:54px; height:54px; font-size:1.5rem; }
    .btn { min-height:48px; padding:12px 16px; font-size:0.88rem; }
    .menu-item { min-height:52px; padding:14px 18px; font-size:0.9rem; }
    .tab-btn { min-height:48px; padding:12px 16px; }
    h1 { font-size:1.4rem; }
    h2 { font-size:1.2rem; }
    .topbar-left h2 { font-size:0.95rem; }
    .card-title { font-size:1rem; }
    .content { padding:8px 10px; }
    .topbar { padding:8px 10px; }
    .topbar-right .topbar-badge { display:none; }
    .topbar-sync-badge { display:none; }
    .topbar-role { display:none; }
    .topbar-right { gap:6px; }
    .form-grid { grid-template-columns:1fr; gap:10px; }
    .card { padding:12px; border-radius:8px; overflow-x:auto; }
    .modal { padding:14px; max-height:95vh; border-radius:14px 14px 0 0; }
    .modal-overlay { padding:0; align-items:flex-end; }
    .modal-title { font-size:1rem; padding-bottom:10px; }
    .stats-row { grid-template-columns:repeat(2,1fr) !important; gap:8px; }
    .stat-card { padding:14px; }
    .stat-value { font-size:1.6rem; }
    table { font-size:0.7rem; min-width:100%; }
    th,td { padding:4px 6px; }
    .cal-day { min-height:60px; font-size:0.7rem; }
    .cal-event { font-size:0.65rem; padding:2px 4px; }
    .org-container { flex-direction:column; align-items:center; gap:16px; }
    .org-node { max-width:100%; min-width:200px; margin:0 auto; }
    .org-connector { height:16px; }
    .tabs { overflow-x:auto; overflow-y:hidden; -webkit-overflow-scrolling:touch; scrollbar-width:none; flex-wrap:nowrap; }
    .tabs::-webkit-scrollbar { display:none; }
    .tab-btn { white-space:nowrap; flex-shrink:0; font-size:0.8rem; }
    .search-bar { flex-direction:column; gap:8px; }
    .search-bar input,.search-bar select,.search-bar button { width:100%; min-width:unset; }
    .workflow-steps { flex-direction:column; gap:8px; }
    .wf-step { font-size:0.8rem; }
    .wf-step:not(:last-child)::after { display:none; }
    .planning-week-grid { overflow-x:auto; -webkit-overflow-scrolling:touch; display:block; }
    .planning-week-grid > div:first-child { position:sticky; left:0; z-index:1; background:var(--card); }
    .two-col-grid { grid-template-columns:1fr !important; }
    .btn-group { flex-wrap:wrap; gap:6px; }
    .perso-header { padding:14px 16px; gap:12px; border-radius:10px; flex-wrap:wrap; }
    .perso-name { font-size:1rem; }
    .perso-sub { font-size:0.78rem; }
    .perso-avatar { width:44px; height:44px; font-size:1.1rem; }
    .doc-card { flex-direction:column; gap:10px; }
    .doc-actions { flex-direction:row; align-items:center; justify-content:flex-start; flex-wrap:wrap; }
    input,select,textarea { font-size:16px; }
  }

  /* ── SMARTPHONE PAYSAGE (481-768px landscape) ── */
  @media (min-width:481px) and (max-width:768px) and (orientation:landscape) {
    .sidebar { width:200px; }
    .sidebar.open { width:200px; }
    .stats-row { grid-template-columns:repeat(2,1fr) !important; }
    .form-grid { grid-template-columns:1fr 1fr; }
    .modal { max-width:90%; max-height:85vh; }
    .cal-day { min-height:40px; }
  }

  /* ── SAFE AREA iOS (iPhone X+) ── */
  @supports (padding:max(0px)) {
    @media (max-width:768px) {
      .sidebar { padding-left:max(20px,env(safe-area-inset-left)); padding-right:max(20px,env(safe-area-inset-right)); }
      .topbar { padding-left:max(14px,env(safe-area-inset-left)); padding-right:max(14px,env(safe-area-inset-right)); }
      .mobile-menu-btn { bottom:max(18px,env(safe-area-inset-bottom)); right:max(18px,env(safe-area-inset-right)); }
    }
  }

  /* ── PLANNING HORAIRES ── */
  .planning-week-grid { display:grid; grid-template-columns: 80px repeat(7,1fr); border:1px solid var(--border); border-radius:10px; overflow:hidden; }
  .planning-slot { border-radius:4px; font-size:0.65rem; padding:2px 4px; font-weight:600; overflow:hidden; cursor:pointer; }

  /* ── ORGANIGRAMME ── */
  .org-node { background:var(--card); border:2px solid var(--border); border-radius:10px; padding:12px 16px; text-align:center; min-width:130px; max-width:160px; transition:all 0.2s; cursor:pointer; box-shadow:0 2px 8px rgba(0,48,135,0.06); }
  .org-node:hover { border-color:var(--cyan); box-shadow:0 4px 16px rgba(0,174,239,0.15); transform:translateY(-2px); }
  .org-node.top { background:linear-gradient(135deg,var(--bleu),#005299); color:#fff; border-color:var(--bleu); }
  .org-connector { width:2px; height:24px; background:var(--border); margin:0 auto; }

  /* ── KPI CHARTS ── */
  .kpi-bar-wrap { background:var(--bg3); border-radius:20px; height:10px; overflow:hidden; flex:1; }
  .kpi-bar-fill { height:100%; border-radius:20px; transition:width 0.8s ease; }
  .kpi-donut-wrap { position:relative; width:80px; height:80px; flex-shrink:0; }
  .kpi-donut-wrap svg { position:absolute; inset:0; transform:rotate(-90deg); }
  .kpi-donut-val { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-size:0.85rem; font-weight:700; color:var(--bleu); }
  .kpi-mini-bar { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
  .kpi-mini-label { font-size:0.72rem; color:var(--text); min-width:90px; font-weight:600; }
  .kpi-mini-val { font-size:0.72rem; font-weight:700; color:var(--bleu); min-width:38px; text-align:right; }

  /* ── BILAN SOCIAL ── */
  .bs-section { margin-bottom:28px; }
  .bs-table th { background:var(--bleu); color:#fff; }
  .bs-highlight { background:rgba(0,174,239,0.08); font-weight:700; }

  /* ── EXPORT ── */
  .export-card { background:var(--card); border:1.5px solid var(--border); border-radius:12px; padding:20px; text-align:center; cursor:pointer; transition:all 0.15s; }
  .export-card:hover { border-color:var(--cyan); box-shadow:0 4px 16px rgba(0,174,239,0.15); transform:translateY(-2px); }
  .export-icon { font-size:2.4rem; margin-bottom:10px; }
  .export-title { font-size:0.92rem; font-weight:700; color:var(--bleu); margin-bottom:4px; }
  .export-desc { font-size:0.75rem; color:var(--muted); }

  /* ── PRINT ── */
  @media print {
    .sidebar, .topbar, .mobile-menu-btn, .btn, .btn-group, .no-print { display:none!important; }
    .main { width:100%; }
    .content { padding:0; }
    .card { box-shadow:none; break-inside:avoid; }
    body { background:#fff; }
  }
`;

// ─── CONSTANTES ──────────────────────────────────────────────────────────────
const SECTIONS = [
  "Arts Martiaux","Athlétisme","Basket","Boxing Club","Cyclisme",
  "Cyclo-Tourisme","Football","Gym. Rythmique","Gym. Volontaire",
  "Natation","Pétanque","Rugby","Sports Loisirs","Tennis","Omnisport"
];
const COLORS_AV = ["#003087","#00AEEF","#1a7f37","#b45309","#7c3aed","#0077bb","#b91c1c","#0e7490"];
const avatarColor = s => COLORS_AV[(s||"A").charCodeAt(0) % COLORS_AV.length];
const initials    = (p,n) => `${(p||"?")[0]}${(n||"?")[0]}`.toUpperCase();
const formatDate  = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const today       = () => new Date().toISOString().split('T')[0];
const daysUntil   = d => d ? Math.round((new Date(d)-new Date())/86400000) : null;
const expiryStatus= d => { const n=daysUntil(d); if(n===null) return null; if(n<0) return 'expired'; if(n<=60) return 'expiring'; return 'ok'; };
const DELAI_MAX_JOURS = 7;
const getDelaiInfo = (conge) => {
  const s = conge.statut;
  const managers = conge.managers || [];
  if (!s.startsWith('En attente M')) return null;
  const rank = parseInt(s.replace('En attente M', ''));
  const m = managers[rank - 1];
  if (!m) return null;
  const dateRef = rank === 1 ? conge.dateSoumission : conge.datePassageM2 || conge.dateSoumission;
  if (!dateRef) return null;
  const joursEcoules = Math.max(0, Math.floor((new Date() - new Date(dateRef)) / 86400000));
  const joursRestants = Math.max(0, DELAI_MAX_JOURS - joursEcoules);
  const depasse = joursEcoules >= DELAI_MAX_JOURS;
  return { joursEcoules, joursRestants, depasse, managerCourant: m, dateRef, rank };
};

// ─── COMPTES UTILISATEURS ────────────────────────────────────────────────────
// roles : tableau — une personne peut cumuler plusieurs rôles
//   "admin"   → gestion des accès & import données
//   "rh"      → modules RH (congés, staff, médecine, entretiens…)
//   "manager" → validation congés de son équipe
//   "salarie" → espace personnel
// ⚠️ SÉCURITÉ : ces mots de passe seront automatiquement hashés (SHA-256)
// ── Compte admin initial ──────────────────────────────────────────────────────
// Les comptes utilisateurs sont gérés dans Supabase (table cmob_profiles).
// Cette liste locale est vide — elle sert uniquement de valeur par défaut
// le temps que useDB charge les données depuis Supabase au premier render.
const USERS = [];

// ─── DONNÉES EXEMPLES ────────────────────────────────────────────────────────

const SAMPLE_POSTES = [];
const SAMPLE_STAFF = [];
const SAMPLE_EVENTS = [];
// dateSoumission = date de creation de la demande (pour calculer delai M1)
// datePassageM2  = date a laquelle M1 a valide (pour calculer delai M2)
const SAMPLE_CONGES = [];
const SAMPLE_SOLDES = [];
const SAMPLE_DOCS = [];
const SAMPLE_MEDECINE = [];
const SAMPLE_ENTRETIENS = [];

const SAMPLE_BASEDOCS = [];

const SAMPLE_COMM_PERMS = {
  regles: {
    admin:   { peutEcrireA: ['tous'],                   peutPublier: ['tous'] },
    rh:      { peutEcrireA: ['tous'],                   peutPublier: ['tous'] },
    manager: { peutEcrireA: ['equipe','managers','rh'], peutPublier: ['aucun'] },
    salarie: { peutEcrireA: ['managers','rh'],          peutPublier: ['aucun'] },
  },
  exceptions: [],
};

const SAMPLE_ANNONCES = [];
const SAMPLE_MESSAGES = [];


// ══════════════════════════════════════════════════════════════════════════════
// SÉCURITÉ — Hachage des mots de passe & gestion de session
// ══════════════════════════════════════════════════════════════════════════════

// Durée de session : 8 heures (modifiable ici)
const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;
// Avertissement 15 minutes avant expiration
const SESSION_WARN_MS    = 15 * 60 * 1000;

// ── Hachage sécurisé PBKDF2 (Web Crypto natif — équivalent bcrypt en sécurité)
// Format stocké : "pbkdf2$<salt_hex>$<hash_hex>"
// Salt aléatoire 128 bits, 310 000 itérations SHA-256 (recommandation NIST 2023)

const PBKDF2_ITERATIONS = 310_000;
const PBKDF2_ALGO       = 'SHA-256';

async function hashPassword(plain) {
  const salt    = crypto.getRandomValues(new Uint8Array(16));
  const keyMat  = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(plain), 'PBKDF2', false, ['deriveBits']
  );
  const derived = await crypto.subtle.deriveBits(
    { name:'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: PBKDF2_ALGO },
    keyMat, 256
  );
  const saltHex = Array.from(salt).map(b => b.toString(16).padStart(2,'0')).join('');
  const hashHex = Array.from(new Uint8Array(derived)).map(b => b.toString(16).padStart(2,'0')).join('');
  return `pbkdf2$${saltHex}$${hashHex}`;
}

async function verifyPassword(input, stored) {
  if (!stored) return false;

  // Format PBKDF2 (nouveau)
  if (stored.startsWith('pbkdf2$')) {
    const [, saltHex, expectedHex] = stored.split('$');
    const salt   = new Uint8Array(saltHex.match(/.{2}/g).map(b => parseInt(b, 16)));
    const keyMat = await crypto.subtle.importKey(
      'raw', new TextEncoder().encode(input), 'PBKDF2', false, ['deriveBits']
    );
    const derived  = await crypto.subtle.deriveBits(
      { name:'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: PBKDF2_ALGO },
      keyMat, 256
    );
    const hashHex = Array.from(new Uint8Array(derived)).map(b => b.toString(16).padStart(2,'0')).join('');
    // Comparaison en temps constant (protection timing attack)
    if (hashHex.length !== expectedHex.length) return false;
    let diff = 0;
    for (let i = 0; i < hashHex.length; i++) diff |= hashHex.charCodeAt(i) ^ expectedHex.charCodeAt(i);
    return diff === 0;
  }

  // Compat SHA-256 anciens comptes (64 chars hex) — migration transparente
  if (stored.length === 64) {
    const buf  = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    const hash = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,'0')).join('');
    return hash === stored;
  }

  // Mot de passe en clair (très ancien format) — comparaison directe
  return input === stored;
}

// Sauvegarder la session avec timestamp de création
function generateToken() {
  try {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    return Array.from(arr).map(b => b.toString(16).padStart(2,'0')).join('');
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function saveSession(userObj) {
  try {
    // Stocker uniquement l'ID, un token opaque et le timestamp
    // Les rôles et données sensibles ne sont PAS dans localStorage
    const token   = generateToken();
    const session = {
      userId:    userObj.id,
      token,
      createdAt: Date.now(),
      // Données minimales pour reconstituer la session sans les rôles
      login:     userObj.login,
    };
    localStorage.setItem('cmob_session', JSON.stringify(session));
    // Stocker les données complètes en sessionStorage (fermé à la fin de l'onglet)
    sessionStorage.setItem('cmob_user_data', JSON.stringify(userObj));
  } catch {}
}

// Lire la session et vérifier son expiration
function loadSession() {
  try {
    const raw = localStorage.getItem('cmob_session');
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    // Ancien format avec user complet → migrer
    if (parsed.user) {
      const user = parsed.user;
      saveSession(user);
      return { user, expired: false, remainingMs: SESSION_TIMEOUT_MS };
    }

    // Nouveau format : vérifier l'expiration
    const remainingMs = SESSION_TIMEOUT_MS - (Date.now() - (parsed.createdAt || 0));
    if (remainingMs <= 0) {
      localStorage.removeItem('cmob_session');
      sessionStorage.removeItem('cmob_user_data');
      return null;
    }

    // Récupérer les données utilisateur depuis sessionStorage (plus sécurisé)
    const userData = sessionStorage.getItem('cmob_user_data');
    if (!userData) {
      // sessionStorage perdu (onglet fermé/rouvert) → redemander la connexion
      localStorage.removeItem('cmob_session');
      return null;
    }
    const user = JSON.parse(userData);
    return { user, expired: false, remainingMs };
  } catch { return null; }
}

function clearSession() {
  try {
    localStorage.removeItem('cmob_session');
    sessionStorage.removeItem('cmob_user_data');
    // Nettoyer aussi les tentatives de login
    sessionStorage.removeItem('cmob_login_attempts');
  } catch {}
}

const SAMPLE_PROFILS = [];

const SAMPLE_MODELES_CONTRATS = [];
const SAMPLE_CONTRATS_GEN = [];

// ─── STORAGE HOOK ────────────────────────────────────────────────────────────
// ─── CONFIGURATION SUPABASE ──────────────────────────────────────────────────
// Renseignez ces deux valeurs depuis votre tableau de bord Supabase
// Settings → API → Project URL et anon public key
const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || '';

// Client Supabase léger (sans SDK — appels fetch directs pour éviter une dépendance)
const supa = {
  // Lire toutes les lignes d'une table
  from: (table) => ({
    select: () =>
      fetch(`${SUPABASE_URL}/rest/v1/${table}?select=*&order=id`, {
        headers: {
          apikey: SUPABASE_ANON,
          Authorization: `Bearer ${SUPABASE_ANON}`,
        },
      }).then(r => r.ok ? r.json() : Promise.reject(r.status)),

    // Remplacer toutes les données (upsert sur la clé "key")
    upsert: (row) =>
      fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
        method: 'POST',
        headers: {
          apikey: SUPABASE_ANON,
          Authorization: `Bearer ${SUPABASE_ANON}`,
          'Content-Type': 'application/json',
          Prefer: 'resolution=merge-duplicates',
        },
        body: JSON.stringify(row),
      }).then(r => r.ok ? true : Promise.reject(r.status)),
  }),
};

// ─── HOOK useDB (version Supabase) ───────────────────────────────────────────
// Chaque "collection" SIRH est stockée dans une seule ligne Supabase :
//   table "sirh_data" → { key: "cmob_staff", payload: [...] }
const SAMPLE_PLANNING_H = [];

// ─── BASE DOCUMENTAIRE ───────────────────────────────────────────────────────
const CAT_ICONS = {
  "Procédures internes / RH":             { icon:"📋", color:"#003087", bg:"rgba(0,48,135,0.08)" },
  "Conventions collectives":              { icon:"⚖️",  color:"#7c3aed", bg:"rgba(124,58,237,0.08)" },
  "Documents légaux (contrats, avenants)":{ icon:"📜",  color:"#b45309", bg:"rgba(180,83,9,0.08)" },
  "Fiches de poste":                      { icon:"👤",  color:"#0077bb", bg:"rgba(0,119,187,0.08)" },
  "Notes de service":                     { icon:"📝",  color:"#ea580c", bg:"rgba(234,88,12,0.08)" },
  "Documents formation":                  { icon:"🎓",  color:"#1a7f37", bg:"rgba(26,127,55,0.08)" },
};
const ALL_CATS = Object.keys(CAT_ICONS);

// ─── COMMUNICATION ───────────────────────────────────────────────────────────
const DEFAULT_COMM_PERMS = {
  regles: {
    admin:   { peutEcrireA: ['tous'],                   peutPublier: ['tous'] },
    rh:      { peutEcrireA: ['tous'],                   peutPublier: ['tous'] },
    manager: { peutEcrireA: ['equipe','managers','rh'], peutPublier: ['aucun'] },
    salarie: { peutEcrireA: ['managers','rh'],          peutPublier: ['aucun'] },
  },
  exceptions: [],
};

// ─── PROFILS ─────────────────────────────────────────────────────────────────
const PROFIL_CHAMPS_REQUIS = [
  'dateNaissance','lieuNaissance','paysNaissance','nationalite','numeross',
  'adresse','codePostal','ville','telPerso','emailPerso',
  'situationFamiliale','personneUrgence','telUrgence',
  'iban','bic','banque',
];

// ─── CONTRATS ────────────────────────────────────────────────────────────────
const TYPES_CONTRAT = ['CDI', 'CDD', 'CDII', 'Avenant'];

// Types de personnel disponibles (remplace l'ancien type "Salarié" générique)
const TYPES_PERSONNEL = ['CDI', 'CDD', 'CDII', 'Apprenti', 'Stagiaire', 'Alternant', 'Bénévole', 'Dirigeant'];

// Types considérés comme "salariés" (avec contrat de travail)
const TYPES_SALARIE = ['CDI', 'CDD', 'CDII', 'Apprenti', 'Stagiaire', 'Alternant', 'Salarié'];

// Helper : est-ce un salarié (contrat de travail) ?
const isSalarie = (s) => TYPES_SALARIE.includes(s?.type);

const CLUB_INFO = {
  club_nom:          'Club Municipal Omnisport de Bassens (CMOB)',
  club_adresse:      '2 rue Léo Lagrange, 33530 Bassens',
  club_siret:        '',  // Renseigné dans Paramètres → Infos Association
  club_representant: '',  // Récupéré depuis InfosAssociation (président)
};

// ─── Motifs légaux de CDD — Art. L1242-2 CT (liste limitative) ──────────────────
// ⚠️ Un motif invalide peut entraîner la requalification du CDD en CDI
const MOTIFS_CDD = [
  { value: 'remplacement', label: `Remplacement d'un salarié absent (L1242-2 1°)` },
  { value: 'accroissement', label: `Accroissement temporaire d'activité (L1242-2 2°)` },
  { value: 'saisonnier', label: 'Emploi saisonnier (L1242-2 3°)' },
  { value: 'usage_constant', label: 'Usage constant de ne pas recourir au CDI (L1242-2 3°)' },
  { value: 'attente_embauche', label: `En attente de prise de poste d'un CDI (L1242-2 1°)` },
  { value: 'remplacement_chef', label: `Remplacement du chef d'entreprise (L1242-2 1°)` },
  { value: 'contrat_aidé', label: 'Contrat aidé / insertion (L1242-3)' },
];

const CHAMPS_MANUELS = {
  CDI: [
    { k:'date_effet',       label:"Date d'effet",              type:'date',     requis:true },
    { k:'poste',            label:'Intitulé du poste',         type:'text',     requis:true,  placeholder:'Entraîneur Football' },
    { k:'salaire_brut',     label:'Salaire brut mensuel (€)',  type:'number',   requis:true,  placeholder:'2200' },
    { k:'temps_partiel',    label:'Temps partiel',             type:'checkbox', requis:false },
    { k:'taux_occupation',  label:"Taux d'occupation (%)",     type:'text',     requis:true,  placeholder:'80 %', conditionnel:'temps_partiel' },       // Art. L3123-6 CT — obligatoire
    { k:'heures_semaine',   label:'Heures hebdomadaires contractuelles', type:'number', requis:true, placeholder:'28', conditionnel:'temps_partiel' },        // Art. L3123-6 CT — obligatoire
    { k:'heures_annuelles', label:'Heures annuelles contractuelles (h)', type:'number', requis:true, placeholder:'1607', conditionnel:'temps_partiel' },      // Art. L3123-6 CT — obligatoire
    { k:'repartition_heures', label:'Répartition sur les jours de la semaine', type:'text', requis:true, placeholder:'Ex: Lun 9h-13h / Mer 14h-18h / Ven 9h-12h', conditionnel:'temps_partiel' }, // Art. L3123-6 CT
    { k:'horaires',         label:'Horaires de travail',       type:'text',   requis:false, placeholder:'Ex: Lundi-Vendredi 9h-17h' },
    { k:'commentaire',      label:'Clauses particulières',     type:'textarea', requis:false },
  ],
  CDD: [
    { k:'date_effet',       label:'Date de début',             type:'date',     requis:true },
    { k:'duree',            label:'Durée du contrat',          type:'text',     requis:true,  placeholder:'6 mois' },
    { k:'motif',            label:'Motif légal du CDD (Art. L1242-2)', type:'select', requis:true,
      options: MOTIFS_CDD.map(m=>m.label), placeholder:'Sélectionner le motif légal...' },
    { k:'poste',            label:'Intitulé du poste',         type:'text',     requis:true,  placeholder:'Coach Natation' },
    { k:'salaire_brut',     label:'Salaire brut mensuel (€)',  type:'number',   requis:true,  placeholder:'1800' },
    { k:'temps_partiel',    label:'Temps partiel',             type:'checkbox', requis:false },
    { k:'taux_occupation',  label:"Taux d'occupation (%)",     type:'text',     requis:true,  placeholder:'80 %', conditionnel:'temps_partiel' },       // Art. L3123-6 CT — obligatoire
    { k:'heures_semaine',   label:'Heures hebdomadaires contractuelles', type:'number', requis:true, placeholder:'28', conditionnel:'temps_partiel' },        // Art. L3123-6 CT — obligatoire
    { k:'heures_annuelles', label:'Heures annuelles contractuelles (h)', type:'number', requis:true, placeholder:'1607', conditionnel:'temps_partiel' },      // Art. L3123-6 CT — obligatoire
    { k:'repartition_heures', label:'Répartition sur les jours de la semaine', type:'text', requis:true, placeholder:'Ex: Lun 9h-13h / Mer 14h-18h / Ven 9h-12h', conditionnel:'temps_partiel' }, // Art. L3123-6 CT
    { k:'horaires',         label:'Horaires de travail',       type:'text',   requis:false, placeholder:'Ex: 35h/semaine, Lundi-Vendredi' },
    { k:'commentaire',      label:'Clauses particulières',     type:'textarea', requis:false },
  ],
  CDII: [
    { k:'date_effet',       label:"Date d'effet",              type:'date',     requis:true },
    { k:'poste',            label:'Intitulé du poste',         type:'text',     requis:true,  placeholder:'Entraîneur intermittent' },
    { k:'salaire_brut',     label:'Taux horaire brut (€)',     type:'number',   requis:true,  placeholder:'15' },
    { k:'heures_annuelles', label:'Volume horaire annuel garanti (h)', type:'number', requis:true, placeholder:'200' },
    { k:'commentaire',      label:'Clauses particulières',     type:'textarea', requis:false },
  ],
  Avenant: [
    { k:'date_effet',       label:"Date d'effet de l'avenant", type:'date',    requis:true },
    { k:'motif',            label:"Objet de l'avenant",        type:'text',    requis:true,  placeholder:'Modification du salaire / des horaires' },
    { k:'salaire_brut',     label:'Nouveau salaire brut (€)',  type:'number',  requis:false, placeholder:'2400' },
    { k:'temps_partiel',    label:'Passage à temps partiel',   type:'checkbox',requis:false },
    { k:'taux_occupation',  label:"Nouveau taux d'occupation", type:'text',    requis:false, placeholder:'80 %', conditionnel:'temps_partiel' },
    { k:'heures_annuelles', label:'Heures annuelles (h)',      type:'number',  requis:false, placeholder:'1400', conditionnel:'temps_partiel' },
    { k:'horaires',         label:'Nouveaux horaires',         type:'text',    requis:false, placeholder:'28h/semaine' },
    { k:'poste',            label:'Nouveau poste (si changement)', type:'text',requis:false },
    { k:'commentaire',      label:'Détail des modifications',  type:'textarea',requis:true },
  ],
};



// ─── Nouvelles collections ────────────────────────────────────────────────────
const SAMPLE_REMUNERATIONS = [];
const SAMPLE_ABSENCES_NP = [];
const SAMPLE_FORMATIONS = [];
const SAMPLE_BENEVOLES = [];
const SAMPLE_REUNIONS = [];
const SAMPLE_ONBOARDING = [];
const SAMPLE_ALERTES_CONFIG = null; // valeur par défaut gérée dans AlertesConfig.jsx



// ── Multi-sections : normaliser section(s) d'un salarié en tableau ────────────
// Compat : section (string) ET sections (array) coexistent pendant la migration
function getSections(s) {
  if (!s) return [];
  if (Array.isArray(s.sections) && s.sections.length > 0) return s.sections;
  if (s.section) return [s.section];
  return [];
}

// Section principale (première du tableau ou section string)
function getSectionPrincipale(s) {
  const secs = getSections(s);
  return secs[0] || '';
}

// Est-ce que ce salarié appartient à cette section ?
function appartientSection(s, section) {
  return getSections(s).includes(section);
}

// ── Fetch avec timeout automatique ───────────────────────────────
async function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timer);
    return response;
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw new Error(`Délai dépassé (${timeoutMs / 1000}s) — serveur non joignable.`);
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ BARÈMES SOCIAUX — PAS DE CONSTANTES SMIC/CCNS ICI
// Les valeurs SMIC, valeur du point CCNS et plafond SS sont gérées dynamiquement
// par le hook useBaremes (src/hooks/useBaremes.js) qui les récupère depuis :
//   • data.gouv.fr (SMIC officiel)
//   • API Legifrance PISTE (CCNS IDCC 2511 avenants)
//   • Backend CMOB /api/baremes/refresh (agrégateur + cache Supabase)
// En cas d'indisponibilité, les valeurs de repli sont dans BAREMES_FALLBACK
// (src/hooks/useBaremes.js) — NE PAS dupliquer ici.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Exports ─────────────────────────────────────────────────────────────────
export { fetchWithTimeout, getSections, getSectionPrincipale, appartientSection,
  
  LOGO_CMOB, CSS, formatDate, today, daysUntil, expiryStatus,
  avatarColor, initials, SECTIONS,
  USERS, SAMPLE_POSTES, SAMPLE_STAFF, SAMPLE_EVENTS, SAMPLE_CONGES, SAMPLE_SOLDES,
  SAMPLE_DOCS, SAMPLE_MEDECINE, SAMPLE_ENTRETIENS, SAMPLE_BASEDOCS,
  SAMPLE_COMM_PERMS, SAMPLE_ANNONCES, SAMPLE_MESSAGES,
  SAMPLE_PROFILS, SAMPLE_MODELES_CONTRATS, SAMPLE_CONTRATS_GEN,
  SAMPLE_PLANNING_H,
  SESSION_TIMEOUT_MS, SESSION_WARN_MS,
  saveSession, loadSession, clearSession,
  verifyPassword, hashPassword,
  calcJoursConge, calcJoursCongeRetour, MODES_JOURS, MODES_JOURS_DEFAULT,
  SUPABASE_URL, SUPABASE_ANON, supa,
  DELAI_MAX_JOURS, getDelaiInfo,
  CAT_ICONS, ALL_CATS,
  DEFAULT_COMM_PERMS,
  PROFIL_CHAMPS_REQUIS,
  TYPES_CONTRAT, TYPES_PERSONNEL, TYPES_SALARIE, isSalarie, CLUB_INFO, CHAMPS_MANUELS, MOTIFS_CDD,
  SAMPLE_REMUNERATIONS, SAMPLE_ABSENCES_NP, SAMPLE_FORMATIONS,
  SAMPLE_BENEVOLES, SAMPLE_REUNIONS, SAMPLE_ONBOARDING,
  SAMPLE_ALERTES_CONFIG,
  obfuscate, deobfuscate,
  parseExcelDate,
};

// ─── Obfuscation clés API (XOR variable + base64) ────────────────────────────
// Algorithme unique partagé par tous les modules
function obfuscate(str) {
  if (!str) return '';
  try {
    return btoa(Array.from(str).map((c,i) =>
      String.fromCharCode(c.charCodeAt(0) ^ (0x5C ^ (i % 7)))
    ).join(''));
  } catch { return str; }
}

function deobfuscate(str) {
  if (!str) return '';
  try {
    const bytes = atob(str);
    return bytes.split('').map((c,i) =>
      String.fromCharCode(c.charCodeAt(0) ^ (0x5C ^ (i % 7)))
    ).join('');
  } catch { return str; }
}

// ─── Parsing de date robuste (import Excel/CSV) ───────────────────────────────
// Gère : objets Date JS, nombres série Excel, chaînes YYYY-MM-DD, DD/MM/YYYY, etc.
// Toujours en heure locale pour éviter le décalage UTC.
function parseExcelDate(v) {
  if (!v) return '';
  // Nombre série Excel (entier ou décimal, ex: 42622.708...)
  if (typeof v === 'number' || (typeof v === 'string' && /^\d{4,5}(\.\d+)?$/.test(v.trim()) && parseFloat(v) > 1000)) {
    const serial = Math.floor(parseFloat(v));
    // Epoch Excel = 30/12/1899 en UTC
    const ms = Date.UTC(1899, 11, 30) + serial * 86400000;
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
  }
  // Objet Date JS (SheetJS avec cellDates:true) → extraire composantes locales
  if (v instanceof Date) {
    return `${v.getFullYear()}-${String(v.getMonth()+1).padStart(2,'0')}-${String(v.getDate()).padStart(2,'0')}`;
  }
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) { const [d,m,y]=s.split('/'); return `${y}-${m}-${d}`; }
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) { const [d,m,y]=s.split('-'); return `${y}-${m}-${d}`; }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) { const [d,m,y]=s.split('/'); return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`; }
  if (/^\d{8}$/.test(s)) return `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}`;
  return s;
}